package com.appsyde.shared.modules.ticket.data.repository

import com.appsyde.shared.modules.ticket.data.dto.OutboundAttachmentDto
import com.appsyde.shared.modules.ticket.data.source.TicketsRemoteDataSource
import com.appsyde.shared.modules.ticket.data.mapper.toDomain
import com.appsyde.shared.modules.ticket.domain.AssignConflictException
import com.appsyde.shared.modules.ticket.domain.LockedByOtherException
import com.appsyde.shared.modules.ticket.domain.LockNotOwnedException
import com.appsyde.shared.modules.ticket.domain.NotAssigneeException
import com.appsyde.shared.modules.ticket.data.dto.AssignTicketResponseDto
import com.appsyde.shared.modules.ticket.data.dto.AssignUpToResponseDto
import com.appsyde.shared.modules.ticket.data.dto.ReleaseLockResponseDto
import com.appsyde.shared.modules.ticket.data.dto.UnassignTicketResponseDto
import com.appsyde.shared.modules.ticket.domain.model.AcquireLockResult
import com.appsyde.shared.modules.ticket.domain.model.AssignTicketResult
import com.appsyde.shared.modules.ticket.domain.model.AssignUpToResult
import com.appsyde.shared.modules.ticket.domain.model.ReleaseLockResult
import com.appsyde.shared.modules.ticket.domain.model.RenewLockResult
import com.appsyde.shared.modules.ticket.domain.model.SendResult
import com.appsyde.shared.modules.ticket.domain.model.Ticket
import com.appsyde.shared.modules.ticket.domain.model.TicketHistoryItem
import com.appsyde.shared.modules.ticket.domain.model.OutboundAttachmentForSend
import com.appsyde.shared.modules.ticket.domain.model.TicketOriginalHistoryItem
import com.appsyde.shared.modules.ticket.domain.model.TicketRespondState
import com.appsyde.shared.modules.ticket.domain.model.UnassignTicketResult
import com.appsyde.shared.modules.ticket.domain.model.WriteLockDetail
import com.appsyde.shared.modules.ticket.domain.repository.TicketsRepository
import com.appsyde.shared.modules.ticket.data.parseInstantOrNull
import kotlinx.datetime.Instant

class TicketsRepositoryImpl(
    private val remote: TicketsRemoteDataSource
) : TicketsRepository {

    override suspend fun listTickets(limit: Int, assignedToMe: Boolean): List<Ticket> {
        val dto = remote.listTickets(limit = limit, assignedToMe = assignedToMe)
        return dto.tickets.map { it.toDomain() }
    }

    override suspend fun refreshTicketThread(ticketId: String) {
        remote.refreshThread(ticketId)
    }

    override suspend fun getRespondState(ticketId: String): TicketRespondState {
        val dto = remote.getRespond(ticketId)
        val ticket = dto.ticket.toDomain()

        // IA pivot (fallback): derive from messages if backend doesn't expose it in respond DTO
        val lastInboundMessageId = dto.messages
            .asSequence()
            .filter { it.direction.equals("INBOUND", ignoreCase = true) }
            .maxByOrNull { it.sentAtMs }
            ?.id

        return TicketRespondState(
            ticket = ticket,
            messages = dto.messages.map { it.toDomain() },
            draft = dto.draft?.toDomain(),
            lastInboundMessageId = lastInboundMessageId
        )
    }

    override suspend fun updateStatus(ticketId: String, status: String) {
        remote.updateStatus(ticketId = ticketId, status = status)
    }

    override suspend fun addMemory(ticketId: String, key: String, value: String) {
        remote.addMemory(ticketId = ticketId, key = key, value = value)
    }

    override suspend fun confirmKey(ticketId: String, key: String) {
        remote.confirmKey(ticketId = ticketId, key = key)
    }

    override suspend fun sendReply(
        ticketId: String,
        body: String,
        subject: String?,
        to: String?,
        attachments: List<OutboundAttachmentForSend>
    ): SendResult {
        val dtoList = attachments.map {
            OutboundAttachmentDto(
                filename = it.filename,
                mimeType = it.mimeType,
                contentBase64 = it.contentBase64,
                sizeBytes = it.sizeBytes,
                sha256 = it.sha256
            )
        }
        val res = remote.sendReply(
            ticketId = ticketId,
            body = body,
            subject = subject,
            to = to,
            attachments = dtoList
        )

        return SendResult(
            status = res.status,
            ticketId = res.ticketId,
            outboundMessageId = res.outboundMessageId,
            providerMessageId = res.providerMessageId,
            providerThreadId = res.providerThreadId
        )
    }

    override suspend fun getHistory(ticketId: String, limit: Int): List<TicketHistoryItem> {
        val res = remote.getHistory(ticketId, limit = limit)
        return res.items.map { it.toDomain() }
    }

    override suspend fun getOriginalHistory(ticketId: String, limit: Int): List<TicketOriginalHistoryItem> {
        val res = remote.getOriginalHistory(ticketId, limit = limit)
        return res.items.map { it.toDomain() }
    }

    override suspend fun assignTicket(ticketId: String): AssignTicketResult {
        return try {
            val dto = remote.assignTicket(ticketId)
            AssignTicketResult.Success(
                ticketId = dto.ticketId,
                assignedToAccountUserId = dto.assignedToAccountUserId,
                assignedAt = dto.assignedAt,
                changed = dto.changed
            )
        } catch (e: AssignConflictException) {
            AssignTicketResult.AssignedToOther(
                assignedToAccountUserId = e.assignedToAccountUserId,
                assignedToDisplayName = e.assignedToDisplayName
            )
        }
    }

    override suspend fun assignUpTo(count: Int): AssignUpToResult {
        val dto = remote.assignUpTo(count)
        return AssignUpToResult(
            assigned = dto.assigned.map { it.toAssignTicketResult() }
        )
    }

    override suspend fun takeoverTicket(ticketId: String): AssignTicketResult {
        return try {
            val dto = remote.takeoverTicket(ticketId)
            dto.toAssignTicketResult()
        } catch (e: AssignConflictException) {
            AssignTicketResult.AssignedToOther(
                assignedToAccountUserId = e.assignedToAccountUserId,
                assignedToDisplayName = e.assignedToDisplayName
            )
        }
    }

    override suspend fun unassignTicket(ticketId: String): UnassignTicketResult {
        val dto = remote.unassignTicket(ticketId)
        return UnassignTicketResult(ticketId = dto.ticketId, changed = dto.changed)
    }

    override suspend fun acquireLock(ticketId: String): AcquireLockResult {
        return try {
            val dto = remote.acquireLock(ticketId)
            val expiresAt = parseInstantOrNull(dto.writeLock.expiresAt)
                ?: throw IllegalArgumentException("Invalid expiresAt from server: ${dto.writeLock.expiresAt}")
            AcquireLockResult.Success(
                writeLock = WriteLockDetail(
                    accountUserId = dto.writeLock.accountUserId,
                    displayName = dto.writeLock.displayName,
                    expiresAt = expiresAt
                )
            )
        } catch (e: NotAssigneeException) {
            AcquireLockResult.NotAssignee
        } catch (e: LockedByOtherException) {
            val expiresAt = parseInstantOrNull(e.expiresAt)
                ?: throw IllegalArgumentException("Invalid expiresAt from server: ${e.expiresAt}")
            AcquireLockResult.LockedByOther(
                lockedByAccountUserId = e.lockedByAccountUserId,
                lockedByDisplayName = e.lockedByDisplayName,
                expiresAt = expiresAt
            )
        } catch (_: LockNotOwnedException) {
            AcquireLockResult.NotOwnedOrExpired
        }
    }

    override suspend fun renewLock(ticketId: String): RenewLockResult {
        return try {
            val dto = remote.renewLock(ticketId)
            val expiresAt = parseInstantOrNull(dto.expiresAt)
                ?: throw IllegalArgumentException("Invalid expiresAt from server: ${dto.expiresAt}")
            RenewLockResult.Success(expiresAt = expiresAt)
        } catch (e: LockNotOwnedException) {
            RenewLockResult.NotOwnedOrExpired
        }
    }

    override suspend fun releaseLock(ticketId: String): ReleaseLockResult {
        val dto = remote.releaseLock(ticketId)
        return ReleaseLockResult(ticketId = dto.ticketId, changed = dto.changed)
    }
}

private fun AssignTicketResponseDto.toAssignTicketResult(): AssignTicketResult.Success =
    AssignTicketResult.Success(
        ticketId = ticketId,
        assignedToAccountUserId = assignedToAccountUserId,
        assignedAt = assignedAt,
        changed = changed
    )